/**
 * The work queues and the counters behind them.
 *
 * LAYER 1, read-only. Every function here answers "what is left to do", and
 * every one of them means the same thing by it: the ACTIVE LEAVES of a task —
 * the rows that currently tile the audio. That predicate is
 * `types.isActiveLeaf`; before the restructure it was open-coded as
 * `!s.splitAt && !s.obsoleteAt` at every site in this file.
 *
 * NOTE for anyone comparing these with `services/reconcile.ts`: the three
 * functions there (`isTaskResolved`, `areSegmentsResolved`, `toDatasetSegments`)
 * read the FULL row set, not the leaves, so a recut task can be counted
 * differently depending on which module you ask. That is a real bug, tracked as
 * follow-up F3 — deliberately NOT fixed here, because changing it changes what
 * "resolved" means and what Export JSON emits.
 */

import { activeLeaves, isActiveLeaf, isSegmentResolved, type Segment, type TaskProgress } from '../types';
import { getDB, getSegmentsByTask } from './store';

/**
 * Queue order: task first, then position within the task.
 *
 * One comparator, used by all three cross-task queues — they were three
 * byte-identical copies. Note it orders on `index` (the display ordinal), which
 * is what these queues have always done; `activeLeaves` orders on `startSec`,
 * and this sort runs last, so it wins.
 */
const byTaskThenIndex = (a: Segment, b: Segment): number =>
  a.taskId === b.taskId ? a.index - b.index : a.taskId < b.taskId ? -1 : 1;

/* -------- cross-task queues (feed the Cuts / Verify / Correct tabs) -------- */

/**
 * Stage 1 queue: leaves whose CUT has not been human-approved yet. This is the
 * factory's first station — nothing reaches Verify until its boundaries have
 * been signed off, and a later boundary change sends the row back here.
 *
 * CAVEAT, and it is deliberate: the second half of that sentence is aspirational.
 * `getPendingVerifySegments` below does NOT gate on `cutApprovedAt`, so the Cuts
 * and Verify queues OVERLAP and the two tab badges are not disjoint counts.
 */
export async function getPendingCutSegments(): Promise<Segment[]> {
  const db = await getDB();
  const segments = await db.getAllFromIndex('segments', 'by-verify', 'pending');
  return activeLeaves(segments)
    .filter((s) => !s.cutApprovedAt)
    .sort(byTaskThenIndex);
}

/** Every still-unverified segment across all tasks, task-then-time ordered. */
export async function getPendingVerifySegments(): Promise<Segment[]> {
  const db = await getDB();
  const segments = await db.getAllFromIndex('segments', 'by-verify', 'pending');
  // Cuts are good enough out of the pipeline now, so Verify no longer waits
  // for a stage-1 sign-off; the Cuts tab stays available for repairs.
  return activeLeaves(segments).sort(byTaskThenIndex);
}

/**
 * Every segment marked incorrect across all tasks.
 * Pass `{ onlyUncorrected: true }` to hide ones that already have a correction.
 */
export async function getIncorrectSegments(opts?: { onlyUncorrected?: boolean }): Promise<Segment[]> {
  const db = await getDB();
  let segments = activeLeaves(await db.getAllFromIndex('segments', 'by-verify', 'incorrect'));
  if (opts?.onlyUncorrected) segments = segments.filter((s) => !isSegmentResolved(s));
  return segments.sort(byTaskThenIndex);
}

/** Counts behind the tab-bar badges. One read per queue, no full table scan. */
export interface QueueCounts {
  /** Stage 1: leaves whose cut still needs human sign-off. */
  cuts: number;
  /** Segments still awaiting a Verify verdict, across all tasks. */
  pendingVerify: number;
  /** Segments marked incorrect that still have no correction. */
  toCorrect: number;
  /** Tasks sitting in `completed`. */
  completed: number;
}

export async function getQueueCounts(): Promise<QueueCounts> {
  const db = await getDB();
  const [pending, incorrect, completed] = await Promise.all([
    db.getAllFromIndex('segments', 'by-verify', 'pending'),
    db.getAllFromIndex('segments', 'by-verify', 'incorrect'),
    db.countFromIndex('tasks', 'by-status', 'completed'),
  ]);
  const leaves = activeLeaves(pending);
  return {
    cuts: leaves.filter((s) => !s.cutApprovedAt).length,
    pendingVerify: leaves.length,
    toCorrect: incorrect.filter((s) => isActiveLeaf(s) && !isSegmentResolved(s)).length,
    completed,
  };
}

/* ------------------------------------------------------------------ */
/* Progress                                                            */
/* ------------------------------------------------------------------ */

/** Count the segment states of a task (drives progress bars and badges). */
export async function getTaskProgress(taskId: string): Promise<TaskProgress> {
  const segments = activeLeaves(await getSegmentsByTask(taskId));
  const progress: TaskProgress = { total: segments.length, pending: 0, correct: 0, incorrect: 0, resolved: 0 };
  for (const s of segments) {
    if (s.verify === 'pending') progress.pending++;
    else if (s.verify === 'correct') progress.correct++;
    else progress.incorrect++;
    if (isSegmentResolved(s)) progress.resolved++;
  }
  return progress;
}

/**
 * Reconciliation check: is every segment of the task either verified-correct or
 * corrected? A task with zero segments is NOT considered resolved.
 */
export async function areAllSegmentsResolved(taskId: string): Promise<boolean> {
  const segments = activeLeaves(await getSegmentsByTask(taskId));
  if (segments.length === 0) return false;
  return segments.every(isSegmentResolved);
}
